import { useEffect, useState } from "react";
import { getStoredToken } from "../api/client";
import { leadCatBrand } from "../brand/brandConfig";
import { BrandLogo } from "../components/BrandLogo/BrandLogo";
import { LeadCatHeroArtwork } from "../components/LeadCatHeroArtwork/LeadCatHeroArtwork";
import { LeadSourceIcon } from "../components/LeadSourceBadge/LeadSourceBadge";
import "./MarketingHome.css";
import { MarketingDemoSection } from "./MarketingDemoSection";
import { MarketingPricingSection } from "./MarketingPricingSection";
import { fetchPublicCrmPricing, type PublicCrmPricing } from "./publicCrmPricing";
import { redirectToCrmDashboard } from "./publicNavigation";

const integrations = [
  {
    id: "instagram",
    name: "Instagram",
    description: "Capture Instagram Direct Messages and customer comments, then turn them into trackable CRM leads.",
    solutionName: "Instagram CRM",
    solutionDescription: "Turn Instagram enquiries into trackable leads and make follow-ups easier for your team.",
    solutionCta: "Explore Instagram CRM",
    href: "/instagram-crm/",
  },
  {
    id: "facebook",
    name: "Facebook",
    description: "Bring Facebook Messenger and customer Page comments into one organised lead-management workflow.",
    solutionName: "Facebook Lead CRM",
    solutionDescription: "Organise Facebook enquiries, assign ownership and keep every sales opportunity moving.",
    solutionCta: "Explore Facebook CRM",
    href: "/facebook-lead-crm/",
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    description: "With an eligible connected WhatsApp Business account, bring supported customer messages into an assigned lead workflow.",
    solutionName: "WhatsApp CRM",
    solutionDescription: "Bring structure to WhatsApp sales enquiries, ownership and follow-ups.",
    solutionCta: "Explore WhatsApp CRM",
    href: "/whatsapp-crm/",
  },
] as const;

export function MarketingHome() {
  const hasSession = Boolean(getStoredToken());
  const [pricing, setPricing] = useState<PublicCrmPricing | null>(null);
  const [pricingFailed, setPricingFailed] = useState(false);

  useEffect(() => {
    document.title = leadCatBrand.marketing.title;
    if (hasSession) redirectToCrmDashboard();
  }, [hasSession]);

  useEffect(() => {
    if (hasSession) return;
    let active = true;
    fetchPublicCrmPricing()
      .then((result) => {
        if (active) setPricing(result);
      })
      .catch(() => {
        if (active) setPricingFailed(true);
      });
    return () => { active = false; };
  }, [hasSession]);

  if (hasSession) return null;

  return (
    <main className="pm-site">
      <header className="pm-header">
        <nav className="pm-nav" aria-label="Public navigation">
          <a className="pm-brand" href="/" aria-label="LeadCat CRM homepage">
            <BrandLogo product="crm" variant="lockup" size="small" className="pm-brand-logo" />
          </a>
          <div className="pm-nav-actions">
            <a className="pm-nav-link pm-nav-product" href="#solutions">Solutions</a>
            <a className="pm-nav-link pm-nav-pricing" href="#pricing">Pricing</a>
            <a className="pm-nav-link pm-nav-billing" href="/react/billing" target="_blank" rel="noreferrer">Billing</a>
            <a className="pm-nav-link" href="/react/login">Sign in</a>
            <a className="pm-button pm-button-primary pm-button-compact" href="/react/signup">
              Get Started <span aria-hidden="true">→</span>
            </a>
          </div>
        </nav>
      </header>

      <section className="pm-hero" aria-labelledby="pm-hero-title">
        <div className="pm-hero-copy">
          <span className="pm-eyebrow"><i aria-hidden="true" /> Simplicity is our audacity.</span>
          <h1 id="pm-hero-title">Never miss another Instagram, Facebook or WhatsApp enquiry.</h1>
          <p className="pm-hero-intro">
            <strong>LeadCat CRM</strong>
            <span>
              LeadCat helps businesses capture, manage, assign, follow up and convert customer enquiries coming
              from Instagram, Facebook and WhatsApp.
            </span>
          </p>
          <div className="pm-hero-actions">
            <a className="pm-button pm-button-primary" href="/react/signup">
              Get Started <span aria-hidden="true">→</span>
            </a>
            <a className="pm-button pm-button-secondary" href="#demo">
              Watch Demo <span aria-hidden="true">▷</span>
            </a>
            <a
              className="pm-button pm-button-secondary"
              href="https://wa.me/919176189950?text=Hi%2C%20I%20would%20like%20to%20request%20a%20demo%20of%20LeadCat%20CRM."
              target="_blank"
              rel="noreferrer"
            >
              Book a Demo
            </a>
          </div>
          <div className="pm-assurance" aria-label="Workspace activation information">
            <span><i aria-hidden="true">✓</i> Account setup first</span>
            <span><i aria-hidden="true">✓</i> Tenant-private workspace</span>
            <span><i aria-hidden="true">✓</i> Simple, transparent plans</span>
          </div>
        </div>

        <figure className="pm-hero-visual">
          <div className="pm-hero-artwork">
            <LeadCatHeroArtwork
              priority
              alt="LeadCat monitoring messages from Instagram, Facebook and WhatsApp"
              sizes="(max-width: 860px) calc(100vw - 36px), 580px"
            />
          </div>
          <figcaption className="pm-conversation-label">
            <strong>Connect your customer conversations</strong>
            <span className="pm-channel-list">
              {integrations.map((integration) => (
                <span className="pm-channel" key={integration.id}>
                  <LeadSourceIcon source={integration.name} size="small" />
                  {integration.name}
                </span>
              ))}
            </span>
          </figcaption>
        </figure>
      </section>

      <MarketingDemoSection />

      <section className="pm-integrations" id="integrations" aria-labelledby="pm-integrations-title">
        <header className="pm-section-heading">
          <span>Available integrations</span>
          <h2 id="pm-integrations-title">Capture leads from the channels your customers already use</h2>
          <p>Bring customer conversations into one clear workspace without losing the original channel context.</p>
        </header>
        <div className="pm-integration-grid">
          {integrations.map((integration) => (
            <article className="pm-integration-card" key={integration.id}>
              <div className="pm-integration-card-heading">
                <LeadSourceIcon source={integration.name} decorative={false} imageAlt={integration.name} />
                <div><h3>{integration.name}</h3><span>Available</span></div>
              </div>
              <p>{integration.description}</p>
            </article>
          ))}
        </div>
        <p className="pm-integration-note">
          Availability requires an eligible LeadCat workspace and a connected provider account. Platform names
          and symbols identify compatibility only and do not imply sponsorship or endorsement.
        </p>
      </section>

      <section className="pm-solutions" id="solutions" aria-labelledby="pm-solutions-title">
        <header className="pm-section-heading">
          <span>Solutions</span>
          <h2 id="pm-solutions-title">Bring social enquiries into a sales process your team can follow</h2>
          <p>See how LeadCat gives customer interest from each channel clear ownership, follow-up and a visible next step.</p>
        </header>
        <div className="pm-solution-grid">
          {integrations.map((integration) => (
            <a className="pm-solution-card" href={integration.href} key={integration.id}>
              <LeadSourceIcon source={integration.name} decorative />
              <span>
                <h3>{integration.solutionName}</h3>
                <p>{integration.solutionDescription}</p>
                <strong className="pm-solution-cta">{integration.solutionCta} <span aria-hidden="true">→</span></strong>
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="pm-value-journey" aria-labelledby="pm-journey-title">
        <header className="pm-journey-heading">
          <span>The LeadCat promise</span>
          <h2 id="pm-journey-title">Lead. Connect. Convert.</h2>
        </header>
        <div className="pm-value-strip">
          <article className="pm-value-card"><span>Lead</span><div><strong>Every enquiry. One clear pipeline.</strong><small>Instagram, Facebook, WhatsApp and manual leads land in one tenant-private workspace.</small></div></article>
          <article className="pm-value-card"><span>Connect</span><div><strong>Know exactly what needs attention.</strong><small>Assign owners, see new leads and find overdue follow-ups without searching.</small></div></article>
          <article className="pm-value-card"><span>Convert</span><div><strong>Make it easier to buy.</strong><small>Follow up with discipline, then give ready customers a direct route to the relevant buying destination.</small></div></article>
        </div>
        <p className="pm-commerce-signal"><strong>From enquiry to sale.</strong> Capture the enquiry, manage the follow-up and, with LeadCat Commerce enabled, move a ready customer toward your storefront. <a href="/social-commerce/">Explore social commerce CRM →</a></p>
      </section>

      <MarketingPricingSection
        pricing={pricing}
        loading={!pricing && !pricingFailed}
        failed={pricingFailed}
      />

      <section className="pm-billing-hero" aria-labelledby="pm-billing-title">
        <div className="pm-billing-copy">
          <span className="pm-billing-kicker">LeadCat Billing</span>
          <h2 id="pm-billing-title">Simple billing software, without the CRM clutter.</h2>
          <p>Create permanent-numbered bills, save products and rates, download PDFs, and review sales in a focused workspace built for an owner and one staff member.</p>
          <div className="pm-billing-price"><strong>₹799/month</strong><span>or ₹7,990/year · 2 seats included</span></div>
          <a className="pm-button pm-billing-action" href="/react/billing" target="_blank" rel="noreferrer">
            Explore LeadCat Billing <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div className="pm-billing-preview" aria-label="LeadCat Billing software preview">
          <header><BrandLogo product="billing" variant="icon" size="small" /><strong>LeadCat Billing</strong><em>Bill #0042</em></header>
          <div className="pm-billing-customer"><span><small>Customer</small><strong>Acme Retail</strong></span><b>PDF ready</b></div>
          <div className="pm-billing-line heading"><span>Item</span><span>Qty</span><span>Amount</span></div>
          <div className="pm-billing-line"><span>Premium service</span><span>1</span><span>₹4,500</span></div>
          <div className="pm-billing-line"><span>Delivery</span><span>1</span><span>₹500</span></div>
          <footer><span>Permanent bill number</span><strong>Total ₹5,000</strong></footer>
        </div>
      </section>

      <footer className="pm-footer">
        <div className="pm-footer-brand">
          <a href="/" aria-label="LeadCat CRM homepage">
            <BrandLogo product="crm" variant="lockup" size="small" className="pm-footer-logo" />
          </a>
          <p>LeadCat CRM is a product from Staminal Technologies.</p>
        </div>
        <div className="pm-footer-meta">
          <p>{leadCatBrand.legalLine}</p>
          <div>
            <span>© {new Date().getFullYear()} Staminal Technologies Private Limited</span>
            <nav aria-label="Legal links">
              <a href="/instagram-crm/">Instagram CRM</a>
              <a href="/facebook-lead-crm/">Facebook CRM</a>
              <a href="/whatsapp-crm/">WhatsApp CRM</a>
              <a href="/social-commerce/">Social Commerce CRM</a>
              <a href="/privacy.html">Privacy</a>
              <a href="/terms.html">Terms</a>
              <a href="/dpa.html">DPA</a>
              <a href="/data-deletion.html">Data deletion</a>
            </nav>
          </div>
        </div>
      </footer>
    </main>
  );
}
